import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Inbox, Check, X, Loader2, Search, FileText,
  ArrowRight, Clock, CheckCircle2, XCircle, Filter,
  Building2, Calendar, ChevronDown, ChevronUp
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EmptyState from "../components/EmptyState";
import { formatIndianDate } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "text-[#D97706]", bg: "bg-[#FEF3C7]", icon: Clock },
  accepted: { label: "Accepted", color: "text-[#059669]", bg: "bg-[#D1FAE5]", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "text-[#DC2626]", bg: "bg-[#FEE2E2]", icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function fmt(amount) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount || 0);
}

export default function ReceivedInvoicesPage() {
  const { defaultCompanyId } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pendingCount, setPendingCount] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/received-invoices/list`, { params, withCredentials: true });
      setInvoices(res.data.invoices || []);
      setPendingCount(res.data.pending_count || 0);
    } catch {
      toast.error("Failed to load received invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices();
  };

  const handleAccept = async (receivedId) => {
    setActionLoading(receivedId);
    try {
      await axios.put(`${API}/received-invoices/${receivedId}/accept`, {}, { withCredentials: true });
      toast.success("Invoice accepted and added to Payables");
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (receivedId) => {
    if (!window.confirm("Reject this invoice? This cannot be undone.")) return;
    setActionLoading(receivedId);
    try {
      await axios.put(`${API}/received-invoices/${receivedId}/reject`, {}, { withCredentials: true });
      toast.success("Invoice rejected");
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setActionLoading(null);
    }
  };

  const filters = [
    { value: "all", label: "All" },
    { value: "pending", label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
    { value: "accepted", label: "Accepted" },
    { value: "rejected", label: "Rejected" },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="received-invoices-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
              <Inbox className="w-6 h-6 text-[#2563EB]" />
              Received Invoices
            </h1>
            <p className="text-sm text-[#6B7280] mt-1">
              Invoices issued to you by other businesses on the platform.
            </p>
          </div>
          {pendingCount > 0 && (
            <div className="px-3 py-1.5 bg-[#FEF3C7] text-[#92400E] rounded-lg text-sm font-medium" data-testid="pending-badge">
              {pendingCount} pending review
            </div>
          )}
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company name or invoice number..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="search-btn">Search</Button>
          </form>
          <div className="flex gap-1 overflow-x-auto" data-testid="status-filters">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                data-testid={`filter-${f.value}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  statusFilter === f.value
                    ? "bg-[#2563EB] text-white"
                    : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={Inbox}
            iconBg="bg-indigo-50 text-indigo-600"
            title="No received invoices"
            description="When other InvoiceSetu businesses share invoices with you, they appear here for one-click claim & payment tracking."
            bullets={[
              "Auto-imports vendor & line items — no re-typing",
              "Mark received bills as paid to trigger MSME compliance",
              "All counter-party invoices stay searchable & exportable",
            ]}
            primaryAction={{ label: "Upload a Bill Manually", to: "/upload" }}
            secondaryAction={{ label: "Browse Sales", to: "/invoices?type=sales" }}
            testId="received-empty-state"
          />
        ) : (
          <div className="space-y-3">
            {invoices.map((inv) => (
              <motion.div
                key={inv.received_id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_2px_8px_rgb(0,0,0,0.03)] overflow-hidden"
                data-testid={`received-invoice-${inv.received_id}`}
              >
                {/* Summary Row */}
                <div
                  className="px-4 sm:px-5 py-4 flex items-center gap-3 cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                  onClick={() => setExpandedId(expandedId === inv.received_id ? null : inv.received_id)}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#EFF6FF] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[#111827] truncate">{inv.sender_company_name || "Unknown Vendor"}</p>
                      <StatusBadge status={inv.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-0.5">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {inv.invoice_number || "—"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {inv.invoice_date ? formatIndianDate(inv.invoice_date) : "—"}
                      </span>
                      {inv.sender_gstin && <span>GSTIN: {inv.sender_gstin}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-[#111827]">{fmt(inv.grand_total)}</p>
                    {inv.due_date && <p className="text-xs text-[#6B7280]">Due {formatIndianDate(inv.due_date)}</p>}
                  </div>
                  {/* Inline Accept/Reject for pending invoices */}
                  {inv.status === "pending" && (
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleAccept(inv.received_id)}
                        disabled={actionLoading === inv.received_id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#059669] text-white hover:bg-[#047857] transition-colors disabled:opacity-50"
                        data-testid={`accept-btn-${inv.received_id}`}
                      >
                        {actionLoading === inv.received_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Accept
                      </button>
                      <button
                        onClick={() => handleReject(inv.received_id)}
                        disabled={actionLoading === inv.received_id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#DC2626] border border-[#FCA5A5] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                        data-testid={`reject-btn-${inv.received_id}`}
                      >
                        <X className="w-3 h-3" />
                        Reject
                      </button>
                    </div>
                  )}
                  <div className="flex-shrink-0 ml-1">
                    {expandedId === inv.received_id
                      ? <ChevronUp className="w-4 h-4 text-[#9CA3AF]" />
                      : <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
                    }
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedId === inv.received_id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-[#F3F4F6]"
                    >
                      <div className="px-4 sm:px-5 py-4 space-y-4">
                        {/* Items Table */}
                        {inv.items && inv.items.length > 0 && (() => {
                          const hasHsn = inv.items.some(it => it.hsn_code);
                          const hasGst = (inv.tax_amount || 0) > 0 || inv.items.some(it => (it.gst_percent || 0) > 0);
                          return (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-[#F9FAFB]">
                                  <th className="text-left px-3 py-2 text-xs font-medium text-[#6B7280]">Item</th>
                                  {hasHsn && <th className="text-left px-3 py-2 text-xs font-medium text-[#6B7280]">HSN</th>}
                                  <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Qty</th>
                                  <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Rate</th>
                                  {hasGst && <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">GST%</th>}
                                  <th className="text-right px-3 py-2 text-xs font-medium text-[#6B7280]">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inv.items.map((item, idx) => (
                                  <tr key={idx} className="border-t border-[#F3F4F6]">
                                    <td className="px-3 py-2 text-[#111827]">{item.name}</td>
                                    {hasHsn && <td className="px-3 py-2 text-[#6B7280]">{item.hsn_code || "—"}</td>}
                                    <td className="px-3 py-2 text-right text-[#111827]">{item.quantity}</td>
                                    <td className="px-3 py-2 text-right text-[#111827]">{fmt(item.unit_price)}</td>
                                    {hasGst && <td className="px-3 py-2 text-right text-[#6B7280]">{item.gst_percent || 0}%</td>}
                                    <td className="px-3 py-2 text-right font-medium text-[#111827]">{fmt(item.total)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          );
                        })()}

                        {/* Totals */}
                        <div className="flex justify-end">
                          <div className="w-64 space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-[#6B7280]">Subtotal</span><span>{fmt(inv.subtotal)}</span></div>
                            {(inv.tax_amount || 0) > 0 && (
                              <div className="flex justify-between"><span className="text-[#6B7280]">Tax</span><span>{fmt(inv.tax_amount)}</span></div>
                            )}
                            <div className="flex justify-between font-bold text-base border-t border-[#E5E7EB] pt-1 mt-1">
                              <span>Total</span><span className="text-[#2563EB]">{fmt(inv.grand_total)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons (already shown inline for pending) */}
                        {inv.status === "accepted" && inv.linked_payable_id && (
                          <div className="flex items-center gap-2 p-3 bg-[#F0FDF4] rounded-lg text-sm">
                            <CheckCircle2 className="w-4 h-4 text-[#059669]" />
                            <span className="text-[#166534]">Accepted — linked to payable invoice <strong>{inv.linked_payable_id}</strong></span>
                          </div>
                        )}

                        {inv.status === "rejected" && (
                          <div className="flex items-center gap-2 p-3 bg-[#FEF2F2] rounded-lg text-sm">
                            <XCircle className="w-4 h-4 text-[#DC2626]" />
                            <span className="text-[#991B1B]">This invoice was rejected.</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
